// ============================================
// PAYMENT DIALOG COMPONENT
// Enhanced payment dialog with cash, card, and mobile money options
// Extracted from POSPageV2.jsx
// ============================================

import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
    CreditCard, Banknote, Smartphone, Check, Gift, Phone 
} from 'lucide-react';
import { cn } from '../../lib/utils';

const PaymentDialog = ({
    open,
    onOpenChange,
    cartTotal,
    itemCount,
    selectedTable,
    paymentMethod,
    setPaymentMethod,
    paymentAmount,
    setPaymentAmount,
    selectedLoyaltyMember,
    getLoyaltyStatusColor,
    getLoyaltyStatusIcon,
    onOpenLoyaltyDialog,
    onSubmitPayment,
}) => {
    // Quick amount buttons for cash
    const quickAmounts = [1000, 2000, 5000, 10000, 20000, 25000, 50000, cartTotal];
    
    // Payment methods configuration
    const paymentMethods = [
        { id: 'cash', icon: Banknote, label: 'Espèces', color: 'green' },
        { id: 'card', icon: CreditCard, label: 'Carte', color: 'blue' },
        { id: 'mobile', icon: Smartphone, label: 'Mobile Money', color: 'orange' },
    ];

    const getMethodColorClasses = (methodId, color) => {
        if (paymentMethod !== methodId) {
            return "border-gray-200 dark:border-gray-700 hover:border-gray-300";
        }
        switch (color) {
            case 'green':
                return "border-green-500 bg-green-50 dark:bg-green-950/50 ring-2 ring-green-500/30";
            case 'blue':
                return "border-blue-500 bg-blue-50 dark:bg-blue-950/50 ring-2 ring-blue-500/30";
            case 'orange':
                return "border-orange-500 bg-orange-50 dark:bg-orange-950/50 ring-2 ring-orange-500/30";
            default:
                return "";
        }
    };

    const getIconBgClasses = (methodId, color) => {
        if (paymentMethod !== methodId) {
            return "bg-gray-100 dark:bg-gray-800 text-gray-500";
        }
        switch (color) {
            case 'green': return "bg-green-500 text-white";
            case 'blue': return "bg-blue-500 text-white";
            case 'orange': return "bg-orange-500 text-white";
            default: return "";
        }
    };

    const isPaymentValid = () => {
        if (cartTotal <= 0) return false;
        if (paymentMethod === 'cash') {
            return paymentAmount && parseFloat(paymentAmount) >= cartTotal;
        }
        return true;
    };

    const calculateChange = () => {
        const paid = parseFloat(paymentAmount) || 0;
        return paid - cartTotal;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl">
                        <CreditCard className="w-6 h-6 text-green-500" />
                        Règlement
                    </DialogTitle>
                </DialogHeader>
                <div className="py-4 space-y-5">
                    {/* Total Amount Display */}
                    <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/40 dark:to-emerald-950/40 rounded-2xl border-2 border-green-200 dark:border-green-800">
                        <div className="text-center">
                            <p className="text-sm text-muted-foreground mb-1">Montant total à régler</p>
                            <p className="text-4xl sm:text-5xl font-black text-green-600 dark:text-green-400">
                                {cartTotal.toLocaleString('fr-FR')} <span className="text-2xl">F</span>
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                                {itemCount} article{itemCount > 1 ? 's' : ''} • {selectedTable ? `Table ${selectedTable.number}` : 'À emporter'}
                            </p>
                        </div>
                    </div>
                    
                    {/* Payment Method Selection */}
                    <div>
                        <label className="text-sm font-bold mb-3 block text-gray-700 dark:text-gray-300">
                            Mode de paiement
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                            {paymentMethods.map(m => (
                                <button
                                    key={m.id}
                                    onClick={() => {
                                        setPaymentMethod(m.id);
                                        if (m.id !== 'cash') setPaymentAmount('');
                                    }}
                                    className={cn(
                                        "flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all",
                                        "touch-manipulation active:scale-95 hover:shadow-lg",
                                        getMethodColorClasses(m.id, m.color)
                                    )}
                                >
                                    <div className={cn(
                                        "w-12 h-12 rounded-full flex items-center justify-center",
                                        getIconBgClasses(m.id, m.color)
                                    )}>
                                        <m.icon className="w-6 h-6" />
                                    </div>
                                    <span className={cn(
                                        "text-sm font-bold",
                                        paymentMethod === m.id ? "text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400"
                                    )}>
                                        {m.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    {/* Cash Payment Section */}
                    {paymentMethod === 'cash' && (
                        <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border">
                            <div>
                                <label className="text-sm font-bold mb-2 block text-gray-700 dark:text-gray-300">
                                    Montant donné par le client
                                </label>
                                <Input
                                    type="number"
                                    inputMode="numeric"
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    className="h-14 text-2xl font-bold text-center border-2"
                                    placeholder={cartTotal.toString()}
                                    data-testid="payment-amount-input"
                                />
                            </div>
                            
                            {/* Quick Amount Buttons */}
                            <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">Montants rapides</p>
                                <div className="grid grid-cols-4 gap-2">
                                    {quickAmounts.map((amount, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setPaymentAmount(amount.toString())}
                                            className={cn(
                                                "py-2.5 px-2 rounded-xl text-sm font-bold transition-all",
                                                "border-2 touch-manipulation active:scale-95",
                                                parseFloat(paymentAmount) === amount
                                                    ? "bg-green-500 border-green-500 text-white"
                                                    : amount === cartTotal
                                                    ? "bg-blue-100 dark:bg-blue-900/50 border-blue-300 text-blue-700 dark:text-blue-300"
                                                    : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300"
                                            )}
                                        >
                                            {amount === cartTotal ? 'Exact' : `${(amount/1000).toFixed(0)}k`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Change Calculation */}
                            <div className={cn(
                                "p-4 rounded-2xl text-center transition-all",
                                !paymentAmount || parseFloat(paymentAmount) < cartTotal
                                    ? "bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800"
                                    : parseFloat(paymentAmount) === cartTotal
                                    ? "bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-200 dark:border-blue-800"
                                    : "bg-green-50 dark:bg-green-950/30 border-2 border-green-200 dark:border-green-800"
                            )}>
                                {!paymentAmount || parseFloat(paymentAmount) < cartTotal ? (
                                    <>
                                        <p className="text-sm font-medium text-red-600 dark:text-red-400">Montant insuffisant</p>
                                        <p className="text-2xl font-black text-red-600 dark:text-red-400 mt-1">
                                            Manque {(cartTotal - (parseFloat(paymentAmount) || 0)).toLocaleString('fr-FR')} F
                                        </p>
                                    </>
                                ) : parseFloat(paymentAmount) === cartTotal ? (
                                    <>
                                        <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Compte exact</p>
                                        <p className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
                                            <Check className="w-7 h-7 inline-block mr-1" /> Pas de monnaie
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm font-medium text-green-600 dark:text-green-400">Rendu monnaie</p>
                                        <p className="text-4xl font-black text-green-600 dark:text-green-400 mt-1">
                                            {calculateChange().toLocaleString('fr-FR')} F
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* Card Payment Section */}
                    {paymentMethod === 'card' && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-2xl border-2 border-blue-200 dark:border-blue-800 text-center">
                            <CreditCard className="w-16 h-16 mx-auto text-blue-500 mb-3" />
                            <p className="text-lg font-bold text-blue-700 dark:text-blue-300">Paiement par carte bancaire</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Le client règle {cartTotal.toLocaleString('fr-FR')} F par carte
                            </p>
                        </div>
                    )}
                    
                    {/* Mobile Money Section */}
                    {paymentMethod === 'mobile' && (
                        <div className="p-4 bg-orange-50 dark:bg-orange-950/30 rounded-2xl border-2 border-orange-200 dark:border-orange-800 text-center">
                            <Smartphone className="w-16 h-16 mx-auto text-orange-500 mb-3" />
                            <p className="text-lg font-bold text-orange-700 dark:text-orange-300">Paiement Mobile Money</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Le client règle {cartTotal.toLocaleString('fr-FR')} F par mobile
                            </p>
                            <div className="flex justify-center gap-3 mt-4">
                                {['Airtel Money', 'Moov Money', 'Orange Money'].map(provider => (
                                    <Badge key={provider} variant="outline" className="text-xs">
                                        {provider}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Loyalty Section - Le Cercle Aldrin's */}
                    <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-2xl border-2 border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                                <Gift className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <p className="font-bold text-green-800 dark:text-green-300 text-sm">Le Cercle Aldrin's</p>
                                <p className="text-xs text-green-600 dark:text-green-400">Programme fidélité</p>
                            </div>
                        </div>
                        
                        {selectedLoyaltyMember ? (
                            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className={cn("p-2 rounded-full", getLoyaltyStatusColor(selectedLoyaltyMember.status))}>
                                        {getLoyaltyStatusIcon(selectedLoyaltyMember.status)}
                                    </div>
                                    <div>
                                        <p className="font-bold text-sm">{selectedLoyaltyMember.first_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {selectedLoyaltyMember.points} pts • Statut {selectedLoyaltyMember.status}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-muted-foreground">Points à gagner</p>
                                    <p className="text-lg font-black text-green-600">+{Math.floor(cartTotal / 1000)}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center">
                                <p className="text-sm text-green-700 dark:text-green-400 mb-2">
                                    Ce client peut gagner <span className="font-bold">+{Math.floor(cartTotal / 1000)} point{Math.floor(cartTotal / 1000) > 1 ? 's' : ''}</span>
                                </p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-green-300 text-green-700 hover:bg-green-100"
                                    onClick={onOpenLoyaltyDialog}
                                >
                                    <Phone className="w-4 h-4 mr-2" />
                                    Rechercher par téléphone
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
                
                <DialogFooter className="gap-2 flex-col sm:flex-row">
                    <Button 
                        variant="outline" 
                        onClick={() => onOpenChange(false)}
                        className="w-full sm:w-auto"
                    >
                        Annuler
                    </Button>
                    <Button 
                        className={cn(
                            "w-full sm:w-auto text-lg font-bold h-14 px-8",
                            !isPaymentValid()
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-green-600 hover:bg-green-700"
                        )}
                        disabled={!isPaymentValid()}
                        onClick={onSubmitPayment}
                        data-testid="confirm-payment-btn"
                    >
                        <Check className="w-5 h-5 mr-2" />
                        Valider {cartTotal.toLocaleString('fr-FR')} F
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PaymentDialog;
